"use client"

import { useEffect, useState } from "react"
import axios from "../api/axios"
import { Users, Activity, FileText, TrendingUp, Calendar, Clock, Database, FolderOpen } from "lucide-react"
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts"

function Dashboard() {
  const [user, setUser] = useState(null)
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalTeams: 0,
    filesUploaded: 0,
  })
  const [files, setFiles] = useState([])
  const [teams, setTeams] = useState([])
  const [fileTypes, setFileTypes] = useState([])
  const [recentFiles, setRecentFiles] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true)

        // Fetch user data
        const userRes = await axios.get("/api/user", { withCredentials: true })
        setUser(userRes.data)

        // Fetch admin stats
        const adminRes = await axios.get("/api/admin-index")
        setStats((prevStats) => ({
          ...prevStats,
          ...adminRes.data,
        }))

        // Fetch teams with files
        const teamsRes = await axios.get("/api/teams_with_files")
        setTeams(teamsRes.data)

        // Extract all files from teams
        const allFiles = teamsRes.data.flatMap((team) =>
          (team.files || []).map((file) => ({
            ...file,
            teamName: team.name,
            teamId: team.id,
          })),
        )
        setFiles(allFiles)

        // Get recent files (last 5)
        const sortedFiles = allFiles
          .filter((file) => file.uploaded_at)
          .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))
          .slice(0, 5)
        setRecentFiles(sortedFiles)

        // Update file count
        setStats((prev) => ({ ...prev, filesUploaded: allFiles.length, totalTeams: teamsRes.data.length }))

        // Fetch file types
        const fileTypesRes = await axios.get("/api/file-type")
        setFileTypes(fileTypesRes.data)
      } catch (err) {
        console.error("Error fetching dashboard data:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchAllData()
  }, [])

  // Generate dynamic pie chart data for file types
  const getFileTypeDistribution = () => {
    const distribution = {}
    files.forEach((file) => {
      const typeName = getFileTypeName(file.file_type_id)
      distribution[typeName] = (distribution[typeName] || 0) + 1
    })

    const colors = [
      "#F79639", // Orange
      "#10B981", // Green
      "#3B82F6", // Blue
      "#8B5CF6", // Purple
      "#F59E0B", // Amber
      "#EF4444", // Red
      "#06B6D4", // Cyan
      "#84CC16", // Lime
      "#F97316", // Orange variant
      "#6366F1", // Indigo
    ]
    return Object.entries(distribution).map(([name, value], index) => ({
      name,
      value,
      color: colors[index % colors.length],
    }))
  }

  // Generate monthly upload trends
  const getMonthlyTrends = () => {
    const monthlyData = {}
    const last6Months = []

    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthKey = date.toLocaleDateString("en-US", { month: "short" })
      last6Months.push(monthKey)
      monthlyData[monthKey] = { files: 0, users: 0 }
    }

    // Count files by month
    files.forEach((file) => {
      if (file.uploaded_at) {
        const fileDate = new Date(file.uploaded_at)
        const monthKey = fileDate.toLocaleDateString("en-US", { month: "short" })
        if (monthlyData[monthKey]) {
          monthlyData[monthKey].files++
        }
      }
    })

    // Simulate user growth (you can replace with real data if available)
    last6Months.forEach((month, index) => {
      monthlyData[month].users = Math.floor(stats.totalUsers * (0.7 + index * 0.05))
    })

    return last6Months.map((month) => ({
      name: month,
      files: monthlyData[month].files,
      users: monthlyData[month].users,
    }))
  }

  const getFileTypeName = (id) => {
    if (id === null || id === undefined) return "No Type"
    const type = fileTypes.find((t) => t.id.toString() === id.toString())
    return type ? type.name : "Unknown"
  }

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const StatCard = ({ icon: Icon, title, value, subtitle, trend, color = "orange" }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {value !== undefined && value !== null ? value.toLocaleString() : "0"}
          </p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          {trend && (
            <div className="flex items-center mt-2">
              <TrendingUp size={16} className="text-green-500 mr-1" />
              <span className="text-sm text-green-600 font-medium">{trend}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color === "orange" ? "bg-orange-100" : `bg-${color}-100`}`}>
          <Icon size={24} className={color === "orange" ? "text-orange-600" : `text-${color}-600`} />
        </div>
      </div>
    </div>
  )

  const RecentActivity = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Recent Files Uploaded</h3>
        <button
          onClick={() => (window.location.href = "/files")}
          className="text-orange-600 hover:text-orange-700 text-sm font-medium"
        >
          View All
        </button>
      </div>
      <div className="space-y-4">
        {recentFiles.length > 0 ? (
          recentFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center space-x-3 p-3 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <div className="p-2 rounded-full bg-orange-100">
                <FileText size={16} className="text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{file.filename || "Unnamed File"}</p>
                <p className="text-xs text-gray-500">
                  by {file.user?.name || "Unknown"} • {file.teamName} • {formatFileSize(file.size)}
                </p>
              </div>
              <div className="flex items-center text-xs text-gray-400">
                <Clock size={12} className="mr-1" />
                {file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString() : "Unknown"}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-4">
            <FileText size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">No recent files</p>
          </div>
        )}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  const pieData = getFileTypeDistribution()
  const barData = getMonthlyTrends()

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Welcome back{user ? `, ${user.name}` : ""}! Here's what's happening today.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Calendar size={16} />
                <span>
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
              <button
                onClick={() => (window.location.href = "/files")}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 shadow-lg"
              >
                <Database size={16} />
                <span>View Files</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Users}
            title="Total Users"
            value={stats.totalUsers}
            subtitle="Registered users"
            color="orange"
          />
          <StatCard
            icon={Activity}
            title="Active Users"
            value={stats.activeUsers}
            subtitle="Currently active"
            color="green"
          />
          <StatCard
            icon={FileText}
            title="Total Files"
            value={stats.filesUploaded}
            subtitle="Uploaded files"
            color="purple"
          />
          <StatCard icon={FolderOpen} title="Teams" value={stats.totalTeams} subtitle="Active teams" color="blue" />
        </div>

        {/* Charts and Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* File Type Distribution Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">File Type Distribution</h3>
            {pieData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <p className="text-gray-500">No file data available</p>
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {pieData.map((entry, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                  <span className="text-xs text-gray-600">
                    {entry.name} ({entry.value})
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Trends */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Trends (Last 6 Months)</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Legend />
                  <Bar dataKey="files" fill="#F79639" name="Files Uploaded" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="users" fill="#FB923C" name="Total Users" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Recent Activity and Team Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <RecentActivity />
          </div>

          {/* Team Overview */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Overview</h3>
            <div className="space-y-4">
              {teams.slice(0, 5).map((team) => (
                <div key={team.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-xs">{team.name?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{team.name}</h4>
                      <p className="text-xs text-gray-500">{team.files?.length || 0} files</p>
                    </div>
                  </div>
                </div>
              ))}

              {teams.length > 5 && (
                <button
                  onClick={() => (window.location.href = "/teammodule")}
                  className="w-full mt-4 bg-orange-50 hover:bg-orange-100 text-orange-700 py-2 px-4 rounded-lg font-medium transition-colors text-sm"
                >
                  View All Teams ({teams.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
